// ============================================================================
// Arithmétique EXACTE du noyau (lot 1 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
//   - ℚ sur BigInt : `Rat` = n / d, d > 0, fraction irréductible. Aucun flottant :
//     0,1 + 0,2 = 0,3 exactement.
//   - Décimaux saisis (« 1,414 ») convertis exactement (1414 / 1000 → 707 / 500).
//   - Radicaux réduits : `Radical` = coef × √rad, rad sans facteur carré (√8 = 2√2).
//     √2 n'est JAMAIS égal à 1,414 ; `isRoundingOf` dit si 1,414 en est un arrondi.
//
// Pur, sans dépendance. Sérialisation JSON : `ratToText` / `ratFromText` (« -3/2 »),
// jamais de BigInt dans un résultat sérialisé.
// ============================================================================

export interface Rat {
  /** numérateur (porte le signe) */
  readonly n: bigint;
  /** dénominateur, toujours > 0 */
  readonly d: bigint;
}

/** Écriture canonique d'un rationnel dans un JSON : « 3 », « -3/2 ». */
export const RAT_TEXT_RE = /^-?(0|[1-9]\d*)(\/[1-9]\d*)?$/;

/** Au-delà, un radicande n'est pas réduit (réponse honnête : null). */
export const MAX_RADICAND = 10n ** 12n;

const MAX_POW = 4096;

function babs(a: bigint): bigint {
  return a < 0n ? -a : a;
}
function gcd(a: bigint, b: bigint): bigint {
  a = babs(a);
  b = babs(b);
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}
function toBig(v: bigint | number): bigint {
  if (typeof v === 'bigint') return v;
  if (!Number.isSafeInteger(v)) throw new RangeError(`entier attendu : ${v}`);
  return BigInt(v);
}

/** n / d réduit. Lève une erreur si d = 0 ou si un nombre n'est pas un entier sûr. */
export function rat(n: bigint | number, d: bigint | number = 1n): Rat {
  let nn = toBig(n);
  let dd = toBig(d);
  if (dd === 0n) throw new RangeError('dénominateur nul');
  if (dd < 0n) {
    nn = -nn;
    dd = -dd;
  }
  const g = gcd(nn, dd) || 1n;
  return { n: nn / g, d: dd / g };
}

export const ZERO: Rat = rat(0);
export const ONE: Rat = rat(1);

export function add(a: Rat, b: Rat): Rat {
  return rat(a.n * b.d + b.n * a.d, a.d * b.d);
}
export function sub(a: Rat, b: Rat): Rat {
  return rat(a.n * b.d - b.n * a.d, a.d * b.d);
}
export function mul(a: Rat, b: Rat): Rat {
  return rat(a.n * b.n, a.d * b.d);
}
/** a ÷ b, null si b = 0 (jamais une valeur inventée). */
export function div(a: Rat, b: Rat): Rat | null {
  if (b.n === 0n) return null;
  return rat(a.n * b.d, a.d * b.n);
}
export function neg(a: Rat): Rat {
  return { n: -a.n, d: a.d };
}
export function abs(a: Rat): Rat {
  return a.n < 0n ? neg(a) : a;
}
export function sign(a: Rat): -1 | 0 | 1 {
  return a.n === 0n ? 0 : a.n < 0n ? -1 : 1;
}
export function cmp(a: Rat, b: Rat): -1 | 0 | 1 {
  const x = a.n * b.d - b.n * a.d;
  return x === 0n ? 0 : x < 0n ? -1 : 1;
}
export function eq(a: Rat, b: Rat): boolean {
  return a.n === b.n && a.d === b.d;
}
export function isZero(a: Rat): boolean {
  return a.n === 0n;
}
export function isInteger(a: Rat): boolean {
  return a.d === 1n;
}
/** a^k pour k entier (|k| ≤ 4096) ; null pour 0 à une puissance négative. */
export function pow(a: Rat, k: number): Rat | null {
  if (!Number.isInteger(k) || Math.abs(k) > MAX_POW) return null;
  if (k < 0) {
    if (a.n === 0n) return null;
    return pow(rat(a.d, a.n), -k);
  }
  const e = BigInt(k);
  return rat(a.n ** e, a.d ** e);
}

/** Écriture JSON canonique : « 3 », « -3/2 ». */
export function ratToText(a: Rat): string {
  return a.d === 1n ? a.n.toString() : `${a.n}/${a.d}`;
}
/** Relit `ratToText` (et toute fraction « p/q » d'entiers, réduite au passage). */
export function ratFromText(s: string): Rat | null {
  if (!RAT_TEXT_RE.test(s)) return null;
  const [p, q] = s.split('/');
  return rat(BigInt(p!), q === undefined ? 1n : BigInt(q));
}

const DECIMAL_RE = /^([+\-−]?)(\d{1,40})(?:[.,](\d{1,40}))?$/;

/**
 * Décimal saisi → rationnel EXACT : « 1,414 » → 707/500, « -0,5 » → -1/2.
 * Accepte la virgule ou le point ; refuse les exposants, espaces et autres écritures.
 */
export function parseDecimal(text: string): Rat | null {
  const m = DECIMAL_RE.exec(text.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const n = BigInt(m[2]! + frac);
  const r = rat(n, 10n ** BigInt(frac.length));
  return m[1] === '-' || m[1] === '−' ? neg(r) : r;
}

/** Valeur approchée (affichage, échantillonnage). Jamais pour décider d'une égalité. */
export function ratToNumber(a: Rat): number {
  return Number(a.n) / Number(a.d);
}

/* ============================== radicaux ============================== */

export interface Radical {
  /** coefficient rationnel */
  readonly coef: Rat;
  /** radicande ≥ 1, sans facteur carré ; 1 = nombre rationnel */
  readonly rad: bigint;
}

/** n = square² × free, free sans facteur carré. null au-delà de MAX_RADICAND. */
export function squarefreePart(n: bigint): { square: bigint; free: bigint } | null {
  if (n < 1n || n > MAX_RADICAND) return null;
  // ≤ 10^12 : la division d'essai tient en nombres flottants exacts (< 2^53)
  let m = Number(n);
  let square = 1;
  let free = 1;
  for (let p = 2; p * p <= m; p += p === 2 ? 1 : 2) {
    let e = 0;
    while (m % p === 0) {
      m /= p;
      e++;
    }
    if (e > 0) {
      square *= p ** Math.floor(e / 2);
      if (e % 2 === 1) free *= p;
    }
  }
  if (m > 1) free *= m;
  return { square: BigInt(square), free: BigInt(free) };
}

/** coef × √rad, réduit (√8 → 2√2, 0 × √5 → 0). null si rad < 0 ou trop grand. */
export function radical(coef: Rat, rad: bigint): Radical | null {
  if (rad < 0n) return null;
  if (coef.n === 0n || rad === 0n) return { coef: ZERO, rad: 1n };
  const sf = squarefreePart(rad);
  if (!sf) return null;
  return { coef: mul(coef, rat(sf.square)), rad: sf.free };
}

/** Le rationnel q vu comme un radical (rad = 1). */
export function fromRat(q: Rat): Radical {
  return { coef: q, rad: 1n };
}

/** √q exact : √(p/q) = √(pq) / q. null si q < 0 ou radicande trop grand. */
export function sqrtRat(q: Rat): Radical | null {
  if (q.n < 0n) return null;
  return radical(rat(1n, q.d), q.n * q.d);
}

export function radNeg(a: Radical): Radical {
  return { coef: neg(a.coef), rad: a.rad };
}
export function radMul(a: Radical, b: Radical): Radical | null {
  return radical(mul(a.coef, b.coef), a.rad * b.rad);
}
/** a + b si le résultat reste UN radical (même radicande, ou un terme nul), sinon null. */
export function radAdd(a: Radical, b: Radical): Radical | null {
  if (a.coef.n === 0n) return b;
  if (b.coef.n === 0n) return a;
  if (a.rad !== b.rad) return null;
  return radical(add(a.coef, b.coef), a.rad);
}
export function radEq(a: Radical, b: Radical): boolean {
  return eq(a.coef, b.coef) && (a.coef.n === 0n || a.rad === b.rad);
}
export function radIsRational(a: Radical): boolean {
  return a.rad === 1n || a.coef.n === 0n;
}

/** Comparaison EXACTE de coef × √rad avec un rationnel q. */
export function radCmpRat(a: Radical, q: Rat): -1 | 0 | 1 {
  if (a.rad === 1n || a.coef.n === 0n) return cmp(a.coef, q);
  const sa = sign(a.coef);
  const sq = sign(q);
  if (sa !== sq) return sa > sq ? 1 : -1;
  // même signe, non nuls : on compare les carrés des valeurs absolues
  const left = mul(mul(a.coef, a.coef), rat(a.rad));
  const right = mul(q, q);
  const c = cmp(left, right);
  return (sa > 0 ? c : -c) as -1 | 0 | 1;
}

/**
 * `approx` est-il un arrondi de `value` à `places` chiffres après la virgule ?
 * |value − approx| ≤ ½ × 10^−places, décidé EXACTEMENT (√2 ≈ 1,414 : oui ; 1,415 : non).
 */
export function isRoundingOf(value: Radical, approx: Rat, places: number): boolean {
  if (!Number.isInteger(places) || places < 0 || places > 30) return false;
  const half = rat(1n, 2n * 10n ** BigInt(places));
  return radCmpRat(value, sub(approx, half)) >= 0 && radCmpRat(value, add(approx, half)) <= 0;
}

function ratMath(a: Rat): string {
  const s = a.n < 0n ? '−' : '';
  const n = babs(a.n).toString();
  return a.d === 1n ? s + n : `${s}${n} ÷ ${a.d}`;
}

/**
 * Écriture mathématique lisible par notre parseur : « 2√2 », « −√3 », « 3√2 ÷ 4 », « 1 ÷ 2 ».
 * (Affichage seulement ; le JSON passe par `ratToText` sur coef et rad.)
 */
export function radToText(a: Radical): string {
  if (radIsRational(a)) return ratMath(a.coef);
  const s = a.coef.n < 0n ? '−' : '';
  const p = babs(a.coef.n);
  const root = `√${a.rad}`;
  const head = p === 1n ? root : `${p}${root}`;
  return a.coef.d === 1n ? s + head : `${s}${head} ÷ ${a.coef.d}`;
}
